import React, { forwardRef, useImperativeHandle, useRef, useState, useMemo } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Tabs from "src/components/Tabs";
import Select from "src/components/Select";
import RACI from "src/components/RACI";
import LovUsers from "src/components/Lov/Users";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import ModalSide from "src/components/Modal/styles";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useAuth } from "src/context/Auth/context";
import { v4 } from "uuid";
import { IRefICategoriesOccurencesModals, ICategoriesOccurences, IComponentICategoriesOccurencesModals} from "./interface";
import addAndChangeSchema  from "./schemas";


const Modals: React.ForwardRefRenderFunction<IRefICategoriesOccurencesModals, IComponentICategoriesOccurencesModals> = ({
	paginationRef
}, ref) => {
	
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {setLoading} = useLoaderBackdrop();
	const [selectedCategory, setSelectedCategory] = useState<ICategoriesOccurences>({} as ICategoriesOccurences);
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { confirm } = useConfirmModal();
	const { user } = useAuth();

	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		control : controlAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange ,
		unregister: unregisterAddOrChange
	} = useForm();

	const submitAddOrChange = async (data: any) => {
		const dataForm = data;
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			const objToInt = (obj: any) => {
				if(typeof obj === "object")
					return parseInt(obj.value, 10);
				if(typeof obj === "string")
					return parseInt(obj, 10);
				if(typeof obj !== "number")
					return null;
				return obj;
			};

			if(data.octba006){
				dataForm.octba006 = data.octba006?.map((oca006: any) => {		
					const oca006Changed = oca006;
					oca006Changed.oca006Id = parseInt(oca006.oca006Id, 10);
					oca006Changed.oca006NotificaParticipante = oca006.oca006NotificaParticipante ? "S" : "N";	
					oca006Changed.oca006Tipo = oca006.oca006Tipo;
					oca006Changed.oca006Efa004Id = objToInt(oca006Changed.oca006Efa004Id);
					oca006Changed.oca006Efa005Id = objToInt(oca006Changed.oca006Efa005Id);
					oca006Changed.hasOneOfTwoValues = (oca006Changed.oca006Efa004Id > 0) || (oca006Changed.oca006Efa005Id > 0);	
					return oca006Changed;
				}).filter((e: any) => e.hasOneOfTwoValues);
			}
	
			if(data.octba005){
				dataForm.octba005.forEach((oca005: any) => {
					const oca005Changed = oca005;
					oca005Changed.oca005Oca003Id = oca005.oca003Id;
					oca005Changed.oca005Utiliza = oca005.oca005Utiliza ? "S": "N";	
					oca005Changed.oca005Dias = oca005.oca005Dias ? parseInt(oca005.oca005Dias, 10) : null;
					oca005Changed.oca005Efa005Id = objToInt(oca005Changed.oca005Efa005Id);
				});
			}

			dataForm.oca004Efa005Id = typeof data.oca004Efa005Id === "object" ? data.oca004Efa005Id.value : data.oca004Efa005Id;
			dataForm.oca004Efa005Id2 = typeof data.oca004Efa005Id2 === "object" ? data.oca004Efa005Id2.value : data.oca004Efa005Id2;

			await addAndChangeSchema(translate).validate(dataForm,{
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "CategoriesOccurrences/Insert",
					body: {
						...dataForm,	
						oca004Ativo: dataForm.oca004Ativo ? "S" : "N",
						oca004Efa005Id: parseInt(dataForm.oca004Efa005Id, 10) ,
						oca004Efa005Id2:  parseInt(dataForm.oca004Efa005Id2, 10),
						oca004NotificaAutoridade: dataForm.oca004NotificaAutoridade ? "S" : "N",
						oca004NotificaResponsavel: dataForm.oca004NotificaResponsavel ? "S" : "N"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "CategoriesOccurrences/Update",
					body: {
						...dataForm,
						oca004Id: selectedCategory.oca004Id,
						oca004Efa005Id: parseInt(dataForm.oca004Efa005Id, 10) ,
						oca004Efa005Id2:  parseInt(dataForm.oca004Efa005Id2, 10),
						oca004Ativo: dataForm.oca004Ativo ? "S" : "N",
						oca004NotificaAutoridade: dataForm.oca004NotificaAutoridade ? "S" : "N",
						oca004NotificaResponsavel: dataForm.oca004NotificaResponsavel ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Categoria de ocorrência adicionada com sucesso") : translate("Categoria de ocorrência alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar categoria de ocorrência") : translate("Erro ao alterar categoria de ocorrência"), addToast);
		}
	};

	const actionButtonAdd = () => {
		getStandartSteps();
		setIsAdd(true); 
		resetAddOrChange(); 	
		setSelectedCategory({} as ICategoriesOccurences); 	
	};

	const actionButtonChange = (row: ICategoriesOccurences) => {
		setIsAdd(false); 
		resetAddOrChange(); 
		getSelectedCategoryData(row.oca004Id);		
	};

	const getStandartSteps = async () => {

		try{
			setLoading(true);
			const dataController = await RCCServicesApi.getRequest({
				method: "StepsOccurrences/ListActive"
			});
	
			const quadrants = dataController.data.map((x:any) => ({
				oca003Id: x.oca003Id,
				oca003Descricao: x.oca003Descricao
			}));
	
			append(quadrants);
			setLoading(false);
			modalAddOrChange.current?.openModal(); 
		}catch(err){
			standardErrorMessages(err, translate("Error loading standard steps"), addToast);
			setLoading(false);
		}
	};

	const getSelectedCategoryData = async (id: number) => {
		try{
			setLoading(true);

			const dataController = await RCCServicesApi.getRequest({
				method: "CategoriesOccurrences/Select",
				urlParameters: {
					"id": id
				}
			});
	
			const quadrants = dataController.data.octba005.map((x:any) => ({
				oca005Id: x.oca005Id,
				oca003Descricao: x.oca003Descricao,
				oca005Dias: x.oca005Dias,
				oca005Oca004Id: x.oca005Oca004Id,
				oca005Oca003Id: x.oca005Oca003Id,
				oca005Efa005Id : x.oca005Efa005Id,
				efa005Nome: x.efa005Nome,
				utiliza: x.oca005Utiliza
			}));

			append(quadrants);
			setSelectedCategory(dataController.data);
			setLoading(false);	
			modalAddOrChange.current?.openModal(); 
			
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar categoria"), addToast);
			setLoading(false);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));	

	const actionButtonDelete = (row: ICategoriesOccurences) => {
		confirm(
			translate("Exclusão de categoria de ocorrência"),
			`${translate("Deseja realmente excluir a categoria de ocorrência")}: ${row.oca004Descricao}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "CategoriesOccurrences/Delete",
					urlParameters: {
						id: row.oca004Id
					}
				}); 
				
				addToast({
					color: "green",
					description: translate("Categoria de ocorrência excluída com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, "Erro ao excluir categoria de ocorrência", addToast);
			},
			true
		);
	};

	const { 
		fields, 
		append
	} = useFieldArray({
		control: controlAddOrChange,
		name: "octba005"
	});

	const switchUses = (defaultValueUses: string| undefined) => {
		if(defaultValueUses === undefined){
			if(isAdd) return true;
			return false;
		}
		if(defaultValueUses === "S" || defaultValueUses === "N"){
			if(defaultValueUses === "S") return true;
			return false;
		}
		if(isAdd) return true;
		return false;
	};


	const Steps = useMemo(() => (
		<>
			{fields.map((period: any,index) => (
				<RCCGrid key={v4()}>
					<RCCGrid container style={{ padding: "1rem 0rem"}} >
						<RCCInput 
							name={`octba005.${index}.oca003Descricao`}
							label={translate("Etapa")}
							defaultValue={period.oca003Descricao}
							disabled
						/>
						<LovUsers 
							control={controlAddOrChange}
							name={`octba005.${index}.oca005Efa005Id`}
							label={translate("Responsável")}
							defaultValue={{
								value: period.oca005Efa005Id ?? `${user.efa005Id}`, 
								label: period.efa005Nome ?? `${user.efa005Nome}`
							}}
							error={errorsAddOrChange.octba005 && errorsAddOrChange.octba005[index]?.oca005Efa005Id}
							required
						/>
						<RCCGrid
							container
							item
							md={3}
							xs={12}
						>
							<RCCGrid
								container
								item
								md={10}
							>
								<RCCInput 
									register={registerAddOrChange}
									name={`octba005.${index}.oca005Dias`}
									label={translate("Dias")}
									type="number"
									min={0}
									max={99999}
									defaultValue={period.oca005Dias || null}
									error={errorsAddOrChange.octba005 && errorsAddOrChange.octba005[index]?.oca005Dias}
								/>
							</RCCGrid>
						</RCCGrid>
						<RCCGrid
							container
							item
							md={3}
							xs={12}
						>
							<RCCGrid
								container
								item
								md={10}
							>
								<RCCSwitch 
									register={registerAddOrChange}
									label={translate("Utiliza")} 
									name={`octba005.${index}.oca005Utiliza`}	
									defaultChecked={switchUses(period.utiliza)}
								/>
							</RCCGrid>	
						</RCCGrid>
					</RCCGrid>
					<hr/>
				</RCCGrid>
			))}
		</>
	),[fields]);


	return (
		<>
			
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Inserção de categoria de ocorrência") : translate("Alterar categoria de ocorrência"),
					icon: {
						name: "infoCircle"
					},
					subtitle: !isAdd ? `${translate("Ocorrência")}: ${selectedCategory.oca004Descricao}`: undefined
				}}

				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs 
					name="TabOccurrencesCategories"
					tabInit={0}
					options={
						[
							{
								label: translate("Geral"),
								content: (
									<>
										<RCCGrid container style={{ marginBottom: "15px" }}>
											<RCCInput 
												name="oca004Descricao"
												register={registerAddOrChange}
												label={translate("Descrição")}
												maxLength={100}
												defaultValue={selectedCategory.oca004Descricao}
												error={errorsAddOrChange.oca004Descricao}
												required
											/>
										</RCCGrid>
										<RCCGrid container style={{ marginBottom: "15px" }}>
											<Select 
												control={controlAddOrChange}
												name="oca004ExigeAnalise" 
												options={[
													{value: "S", label: translate("Sim")},
													{value: "N", label: translate("Não")}				
												]} 
												defaultValue={selectedCategory.oca004ExigeAnalise ? String(selectedCategory.oca004ExigeAnalise) : "S"}
												submit="value"
												error={errorsAddOrChange.oca004ExigeAnalise}
												label={translate("Exibe análise")}
												required
											/>
										</RCCGrid>				
										<RCCGrid container style={{ marginBottom: "15px" }}>
											<RCCSwitch 
												name="oca004Ativo" 
												label={translate("Ativo")} 
												register={registerAddOrChange} 
												defaultChecked={selectedCategory.oca004Ativo === "S" || isAdd}
											/>	
										</RCCGrid>	
									 	
									</>
								)
							},
							{
								label: translate("RACI"),
								content: (
									<>
										<RACI 
											control={controlAddOrChange}
											register={registerAddOrChange}
											unregister={unregisterAddOrChange}
											clearErrors={clearErrorsAddOrChange}
											error={errorsAddOrChange}
											participants={{
												nameOfObject: "octba006",		
												nameOfID: "oca006Id",		
												nameOfCheckbox: "oca006NotificaParticipante",
												nameOfSelectGroup:"oca006Efa004Id",
												nameOfSelectGroupDescription: "oca006Efa004Nome",
												nameOfSelectUser: "oca006Efa005Id",
												nameOfSelectUserDescription: "oca006Efa005Nome",
												nameOfType: "oca006Tipo",
												defaultValue: selectedCategory.octba006
											}}
											responsible={{
												nameOfSelect: "oca004Efa005Id",
												nameOfCheckbox: "oca004NotificaResponsavel",
												defaultValue: {
													select: selectedCategory.oca004Efa005Id ? selectedCategory.oca004Efa005Id.toString(): "",
													checkbox: selectedCategory.oca004NotificaResponsavel || "S"
												}
											}}
											accountable={{
												nameOfSelect: "oca004Efa005Id2",
												nameOfCheckbox: "oca004NotificaAutoridade",
												defaultValue: {
													select: selectedCategory.oca004Efa005Id2 ? selectedCategory.oca004Efa005Id2.toString() : "",
													checkbox: selectedCategory.oca004NotificaAutoridade || "S"
												}
											}}
										/>
									</>
									
								)
							},{
								label: translate("Etapas"),
								content: (
									<>
										{Steps}
									</>
								)
							}
						]
					}
				/>	
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
